import React, { FC, useEffect, useRef } from 'react';
import { Button, Checkbox, Form, Input, Modal, Space } from 'antd';
import { TextAreaRef } from 'antd/es/input/TextArea';
import { IModal } from 'common/models/model.interface';
import { FormWrapper } from 'common/components/form-wrapper/form-wrapper';
import vehicleService from 'common/services/vehicle-service/vehicle.service';
import { ReactComponent as RejectIcon } from 'assets/icons/reject.svg';
import styles from '../vehicles.module.scss';
import { rejectVehicleValidation } from './reject-vehicle.validation';
import { notification } from 'common/utils/notification';
import { observer } from 'mobx-react-lite';
import configurationStore from 'common/stores/configuration/configuration.store';

const OTHER_REASON_VALUE = 'other';
const OTHER_REASON_TITLE = 'Other';

const initValues = {
  reasons: [],
  otherReason: '',
};

const RejectVehicleModal: FC<IModal<{ vehicleId: number }, any>> = observer(({ isOpen, onClose, title, initData }) => {
  const [form] = Form.useForm();
  const selectedReasons: (number | string)[] | undefined = Form.useWatch('reasons', form);
  const isOtherReasonSelected = !!selectedReasons?.includes(OTHER_REASON_VALUE);
  const otherReasonRef = useRef<TextAreaRef>(null);

  const hideModal = (isSuccess: boolean) => {
    form.resetFields();
    notification.destroy();
    if (onClose) {
      onClose(isSuccess);
    }
  };

  const onFinish = async (values: any) => {
    if (!initData?.vehicleId) {
      console.error('Vehicle id is empty');
      return;
    }

    const reasons = values.reasons?.map((value: number | string) =>
      value === OTHER_REASON_VALUE
        ? { reason: OTHER_REASON_TITLE, description: values.otherReason.trim() }
        : configurationStore.configuration?.rejectVehicleReasons[value as number],
    );

    await vehicleService.reject(initData.vehicleId, { reasons });
    hideModal(true);
  };

  useEffect(() => {
    if (form && isOpen) {
      form.setFieldsValue(initValues);
    }
  }, [isOpen, form, initData]);

  useEffect(() => {
    if (isOtherReasonSelected) {
      otherReasonRef.current?.resizableTextArea?.textArea.scrollIntoView({ block: 'center', behavior: 'smooth' });
      otherReasonRef.current?.focus({ preventScroll: true });
    }
  }, [isOtherReasonSelected]);

  return (
    <Modal centered title={title} width={750} open={isOpen} wrapClassName="custom-modal" closable={false}>
      <FormWrapper form={form} onFinish={onFinish} initialValues={initValues} layout="vertical">
        <Space className="full-width">
          <Form.Item name="reasons" rules={rejectVehicleValidation.reason}>
            <Checkbox.Group
              className="vertical-checkbox-group"
              options={[
                ...(configurationStore.configuration?.rejectVehicleReasons?.map((item, index) => ({
                  value: index,
                  label: (
                    <span>
                      <span className={styles.rejectReason}>{item.reason}</span>
                      {item.description && `: ${item.description}`}
                    </span>
                  ),
                })) ?? []),
                {
                  value: OTHER_REASON_VALUE,
                  label: <span className={styles.rejectReason}>{OTHER_REASON_TITLE}</span>,
                },
              ]}
              style={{ gap: '10px' }}
            />
          </Form.Item>
        </Space>
        {isOtherReasonSelected && (
          <Form.Item name="otherReason" rules={rejectVehicleValidation.otherReason}>
            <Input.TextArea
              ref={otherReasonRef}
              rows={4}
              maxLength={1000}
              className={styles.otherReasonTextArea}
              placeholder="Describe the reason for rejection"
            />
          </Form.Item>
        )}
        <Space className="footer">
          <Button type="link" onClick={() => hideModal(false)} className={styles.modalButton}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" icon={<RejectIcon />} className={`${styles.rejectButton} ${styles.modalButton}`} danger>
            Reject
          </Button>
        </Space>
      </FormWrapper>
    </Modal>
  );
});

export default React.memo(RejectVehicleModal, (prevProps, nextProps) => {
  return prevProps.isOpen === nextProps.isOpen;
});
